import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createChart, CandlestickSeries, CrosshairMode, LineStyle } from "lightweight-charts";
import { Loader2, AlertTriangle, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { PnL } from "./Shared";

const TIMEFRAMES = [["1m", "M1"], ["5m", "M5"], ["15m", "M15"], ["30m", "M30"], ["1h", "H1"], ["4h", "H4"], ["1d", "D1"]];

// Friendly labels for well-known instruments (falls back to the raw broker symbol).
const SYMBOL_NAMES = {
  XAUUSD: "Gold vs US Dollar", XAGUSD: "Silver vs US Dollar", BTCUSD: "Bitcoin vs US Dollar",
  ETHUSD: "Ethereum vs US Dollar", EURUSD: "Euro vs US Dollar", GBPUSD: "Pound vs US Dollar",
  USDJPY: "US Dollar vs Yen", US30: "Dow Jones 30", NAS100: "Nasdaq 100", SPX500: "S&P 500",
};
const friendly = (sym = "") => {
  const base = sym.toUpperCase().replace(/[^A-Z0-9]/g, "");
  for (const key of Object.keys(SYMBOL_NAMES)) if (base.startsWith(key)) return SYMBOL_NAMES[key];
  return "Live market";
};
const precisionFor = (price) => {
  const p = Math.abs(Number(price) || 0);
  if (p === 0) return 2;
  if (p < 20) return 5;
  if (p < 500) return 3;
  return 2;
};
const fmtPrice = (v, prec) => (v == null ? "—" : Number(v).toFixed(prec));

export default function LiveTradingChart({ symbol, symbols = [], position = null, connected = true, onSelectSymbol }) {
  const wrapRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const priceLinesRef = useRef([]);
  const lastKeyRef = useRef("");        // symbol|timeframe currently drawn (to know when to fitContent)
  const reqIdRef = useRef(0);

  const [timeframe, setTimeframe] = useState("5m");
  const [status, setStatus] = useState("loading"); // loading | ready | empty | error
  const [errorMsg, setErrorMsg] = useState("");
  const [lastClose, setLastClose] = useState(null);

  const prec = precisionFor(position?.current_price ?? position?.open_price ?? lastClose ?? 0);
  const currentPrice = position?.current_price ?? lastClose;

  // 1) Create the chart once.
  useEffect(() => {
    if (!wrapRef.current) return;
    let chart;
    try {
      chart = createChart(wrapRef.current, {
        autoSize: true,
        localization: { locale: "en-US" },
        layout: { background: { color: "transparent" }, textColor: "#8b8b96", fontFamily: "JetBrains Mono, monospace" },
        grid: { vertLines: { color: "rgba(255,255,255,0.04)" }, horzLines: { color: "rgba(255,255,255,0.04)" } },
        crosshair: {
          mode: CrosshairMode.Normal,
          vertLine: { color: "rgba(0,240,255,0.35)", width: 1, style: LineStyle.Dashed, labelBackgroundColor: "#0a0a0f" },
          horzLine: { color: "rgba(0,240,255,0.35)", width: 1, style: LineStyle.Dashed, labelBackgroundColor: "#0a0a0f" },
        },
        rightPriceScale: { borderColor: "rgba(255,255,255,0.08)" },
        timeScale: { borderColor: "rgba(255,255,255,0.08)", timeVisible: true, secondsVisible: false },
        handleScroll: true, handleScale: true,
      });
      const series = chart.addSeries(CandlestickSeries, {
        upColor: "#00E676", downColor: "#FF3B30", borderUpColor: "#00E676", borderDownColor: "#FF3B30",
        wickUpColor: "#00E676", wickDownColor: "#FF3B30",
      });
      chartRef.current = chart;
      seriesRef.current = series;
    } catch (e) {
      setStatus("error");
      setErrorMsg("Chart failed to initialise");
    }
    return () => {
      try { chart && chart.remove(); } catch { /* noop */ }
      chartRef.current = null; seriesRef.current = null; priceLinesRef.current = [];
    };
  }, []);

  // 2) Load candles for the active symbol/timeframe (+ light polling).
  const loadCandles = useCallback(async (isPoll = false) => {
    if (!symbol) return;
    const key = `${symbol}|${timeframe}`;
    const myReq = ++reqIdRef.current;
    if (!isPoll) setStatus((s) => (s === "ready" ? s : "loading"));
    try {
      const { data } = await api.get("/investor/candles", { params: { symbol, timeframe, limit: 300 } });
      if (myReq !== reqIdRef.current) return; // superseded
      const rows = data.candles || [];
      const series = seriesRef.current;
      if (!series) return;
      if (rows.length === 0) {
        setStatus("empty");
        setErrorMsg(data.error || "Market data unavailable");
        return;
      }
      series.applyOptions({ priceFormat: { type: "price", precision: precisionFor(rows[rows.length - 1].close), minMove: 1 / Math.pow(10, precisionFor(rows[rows.length - 1].close)) } });
      series.setData(rows);
      setLastClose(rows[rows.length - 1].close);
      if (lastKeyRef.current !== key) { chartRef.current?.timeScale().fitContent(); lastKeyRef.current = key; }
      setStatus("ready");
      setErrorMsg("");
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      if (!isPoll && lastKeyRef.current !== key) { setStatus("error"); setErrorMsg("Could not load chart data"); }
    }
  }, [symbol, timeframe]);

  useEffect(() => {
    loadCandles(false);
    const t = setInterval(() => loadCandles(true), 20000);
    return () => clearInterval(t);
  }, [loadCandles]);

  // 3) Draw entry / SL / TP / current-price lines from the REAL position.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    priceLinesRef.current.forEach((l) => { try { series.removePriceLine(l); } catch { /* noop */ } });
    priceLinesRef.current = [];
    const add = (price, color, title, dashed = false) => {
      if (price == null || Number.isNaN(Number(price))) return;
      const line = series.createPriceLine({
        price: Number(price), color, lineWidth: dashed ? 1 : 2,
        lineStyle: dashed ? LineStyle.Dashed : LineStyle.Solid,
        axisLabelVisible: true, title,
      });
      priceLinesRef.current.push(line);
    };
    if (position) {
      const dir = position.type === "buy" ? "BUY" : "SELL";
      add(position.open_price, position.type === "buy" ? "#00E676" : "#FF3B30", `${dir} ${position.volume} @ ${fmtPrice(position.open_price, prec)}`);
      add(position.sl, "#FF3B30", `SL ${fmtPrice(position.sl, prec)}`);
      add(position.tp, "#00E676", `TP ${fmtPrice(position.tp, prec)}`);
    }
    if (currentPrice != null) add(currentPrice, "#00F0FF", `Price ${fmtPrice(currentPrice, prec)}`, true);
  }, [position, currentPrice, prec, status]);

  const options = useMemo(() => {
    const set = new Set();
    const list = [];
    (symbols || []).forEach((s) => { if (s && !set.has(s)) { set.add(s); list.push(s); } });
    if (!set.has("XAUUSD")) list.push("XAUUSD");
    return list;
  }, [symbols]);

  return (
    <div className="card-dark p-4 sm:p-5" data-testid="live-trading-chart">
      {/* Header: symbol + selectors */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-display text-xl sm:text-2xl text-white truncate" data-testid="chart-symbol">{symbol}</span>
              {options.length > 1 && (
                <div className="relative">
                  <select
                    data-testid="chart-symbol-select"
                    value={symbol}
                    onChange={(e) => onSelectSymbol?.(e.target.value)}
                    className="appearance-none bg-white/5 border border-white/10 rounded-lg pl-3 pr-8 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-[#7000FF]/60"
                  >
                    {options.map((s) => <option key={s} value={s} className="bg-[#121212]">{s}</option>)}
                  </select>
                  <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{friendly(symbol)}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="chart-timeframes">
          {TIMEFRAMES.map(([val, label]) => (
            <button
              key={val}
              data-testid={`chart-tf-${label}`}
              onClick={() => setTimeframe(val)}
              className={`shrink-0 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                timeframe === val ? "bg-[#7000FF]/20 border border-[#7000FF]/50 text-[#00F0FF]" : "border border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/5"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Position summary for the selected symbol */}
      {position ? (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3 mb-4" data-testid="chart-position-summary">
          <SummaryCell label="Type" value={<span className={position.type === "buy" ? "text-[#00E676]" : "text-[#FF3B30]"}>{position.type.toUpperCase()}</span>} />
          <SummaryCell label="Volume" value={`${position.volume} lot`} />
          <SummaryCell label="Entry" value={fmtPrice(position.open_price, prec)} />
          <SummaryCell label="Current" value={fmtPrice(currentPrice, prec)} accent="text-[#00F0FF]" />
          <SummaryCell label="SL / TP" value={`${fmtPrice(position.sl, prec)} / ${fmtPrice(position.tp, prec)}`} small />
          <SummaryCell label="Floating P&L" value={<PnL value={position.profit} />} />
        </div>
      ) : (
        <div className="mb-4 text-xs text-gray-500" data-testid="chart-no-position">
          No open position on <span className="text-gray-300">{symbol}</span> — showing market chart only.
        </div>
      )}

      {/* Chart canvas */}
      <div className="relative w-full h-[400px] sm:h-[480px] rounded-lg overflow-hidden" data-testid="chart-canvas-wrap">
        <div ref={wrapRef} className="absolute inset-0" />
        {(status === "loading" || status === "empty" || status === "error") && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0f]/70 pointer-events-none" data-testid="chart-overlay">
            <div className="text-center px-6">
              {status === "loading" ? (
                <>
                  <Loader2 size={22} className="text-[#7000FF] animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-300">{connected ? "Connecting to MT5…" : "Loading chart…"}</p>
                </>
              ) : (
                <>
                  <AlertTriangle size={22} className="text-[#FFD600] mx-auto mb-2" />
                  <p className="text-sm text-white">{status === "empty" ? "Market data unavailable" : "Chart data error"}</p>
                  <p className="text-xs text-gray-500 mt-1 max-w-xs">{errorMsg || "Live market data for this symbol is temporarily unavailable from your broker."}</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <p className="text-[10px] text-gray-600 mt-2">View-only chart · live data via MetaApi Cloud. Balance, equity & positions reflect your real MT5 account.</p>
    </div>
  );
}

const SummaryCell = ({ label, value, accent = "text-white", small = false }) => (
  <div className="bg-white/[0.03] border border-white/5 rounded-lg px-2.5 py-2">
    <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className={`tabular mt-0.5 ${small ? "text-xs" : "text-sm"} ${accent}`}>{value}</p>
  </div>
);
